import { BusinessProfile, IdealClient, ValueProp, BizSnapshot } from '../types/biz';

/**
 * Creates a business snapshot from business profile, ideal client, and value proposition
 */
export function toBizSnapshot(
  biz: BusinessProfile, 
  ic?: IdealClient, 
  vp?: ValueProp
): BizSnapshot {
  return {
    companyName: biz.companyName,
    industry: biz.industry,
    regions: biz.regions,
    companySize: biz.companySize,
    idealClientTitle: ic?.title,
    promise: vp?.promise,
    elevator: vp?.elevator,
    updatedAt: Date.now()
  };
}

/**
 * Normalizes old profile fields to new business structure
 */
export function normalizeOldProfileFields(profile: any): Partial<BusinessProfile> {
  const normalized: Partial<BusinessProfile> = {};
  
  // Company name
  if (profile.company) {
    normalized.companyName = profile.company;
  }
  
  // Industry - convert string to array if needed
  if (profile.industry) {
    if (Array.isArray(profile.industry)) {
      normalized.industry = profile.industry;
    } else {
      normalized.industry = [profile.industry];
    }
  }
  
  // Regions - convert string to array if needed
  if (profile.region || profile.regions) {
    const regionData = profile.regions || profile.region;
    if (Array.isArray(regionData)) {
      normalized.regions = regionData;
    } else {
      normalized.regions = [regionData];
    }
  }
  
  // Company size
  if (profile.companySize) {
    normalized.companySize = profile.companySize;
  }
  
  // Website
  if (profile.website) {
    normalized.website = profile.website;
  }
  
  // Description
  if (profile.description || profile.bio) {
    normalized.description = profile.description || profile.bio;
  }
  
  return normalized;
}

/**
 * Normalizes social media URLs
 */
export function normalizeSocialUrls(socials: any): any {
  const normalized: any = {};
  
  Object.entries(socials || {}).forEach(([platform, url]) => {
    if (url && typeof url === 'string') {
      // Add https:// if missing
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        normalized[platform] = `https://${url}`;
      } else {
        normalized[platform] = url;
      }
    }
  });
  
  return normalized;
}

/**
 * Validates and normalizes business profile data
 */
export function validateBusinessProfile(data: any): Partial<BusinessProfile> {
  const validated: Partial<BusinessProfile> = {};
  
  // Required fields
  if (data.companyName) {
    validated.companyName = data.companyName.trim();
  }
  
  // Optional fields with validation
  if (data.kvkNumber) {
    validated.kvkNumber = data.kvkNumber.trim();
  }
  
  if (data.website) {
    let website = data.website.trim();
    if (!website.startsWith('http://') && !website.startsWith('https://')) {
      website = `https://${website}`;
    }
    validated.website = website;
  }
  
  if (data.companySize && ['solo', 'smb', 'scaleup', 'enterprise'].includes(data.companySize)) {
    validated.companySize = data.companySize;
  }
  
  if (Array.isArray(data.industry)) {
    validated.industry = data.industry.filter(Boolean);
  }
  
  if (Array.isArray(data.regions)) {
    validated.regions = data.regions.filter(Boolean);
  }
  
  if (data.mode && ['online', 'offline', 'hybrid'].includes(data.mode)) {
    validated.mode = data.mode;
  }
  
  if (Array.isArray(data.languages)) {
    validated.languages = data.languages.filter(Boolean);
  }
  
  if (data.description) {
    validated.description = data.description.trim();
  }
  
  if (data.socials) {
    validated.socials = normalizeSocialUrls(data.socials);
  }
  
  return validated;
}





















